import React from 'react';
import { Calendar, Clock } from 'lucide-react';

// Date range type definition
export interface DateRangeFilter {
  startDate: string | null;
  endDate: string | null;
}

// Date preset type definitions
export type DatePresetId = 
  | 'today' 
  | 'yesterday'
  | 'tomorrow'  // Add the new preset to the type
  | 'thisWeek' 
  | 'lastWeek' 
  | 'thisMonth' 
  | 'lastMonth' 
  | 'last7Days' 
  | 'last30Days' 
  | 'last90Days';

export type DatePresetCategory = 'day' | 'week' | 'month' | 'range';

export interface DatePreset {
  id: DatePresetId;
  category: DatePresetCategory;
  label: {
    th: string;
    en: string;
  };
  getDateRange: () => DateRangeFilter;
  icon: React.ReactNode;
}

// Helper function to get date string in YYYY-MM-DD format
export const getDateString = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Preset definitions organized by category
export const DATE_PRESETS: DatePreset[] = [
  {
    id: 'today',
    category: 'day',
    label: { th: 'วันนี้', en: 'Today' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const dateString = getDateString(today);
      return { startDate: dateString, endDate: dateString };
    }
  },
  {
    id: 'yesterday',
    category: 'day',
    label: { th: 'เมื่อวาน', en: 'Yesterday' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const dateString = getDateString(yesterday);
      return { startDate: dateString, endDate: dateString };
    }
  },
  {
    id: 'tomorrow',
    category: 'day',
    label: { th: 'พรุ่งนี้', en: 'Tomorrow' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      const dateString = getDateString(tomorrow);
      return { startDate: dateString, endDate: dateString };
    }
  },
  {
    id: 'thisWeek',
    category: 'week',
    label: { th: 'สัปดาห์นี้', en: 'This Week' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Start of current week (Sunday)
      return {
        startDate: getDateString(startOfWeek),
        endDate: getDateString(today)
      };
    }
  },
  {
    id: 'lastWeek',
    category: 'week',
    label: { th: 'สัปดาห์ที่แล้ว', en: 'Last Week' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const startOfLastWeek = new Date(today);
      startOfLastWeek.setDate(today.getDate() - today.getDay() - 7); // Start of last week (Sunday)
      const endOfLastWeek = new Date(today);
      endOfLastWeek.setDate(today.getDate() - today.getDay() - 1); // End of last week (Saturday)
      return {
        startDate: getDateString(startOfLastWeek),
        endDate: getDateString(endOfLastWeek)
      };
    }
  },
  {
    id: 'thisMonth',
    category: 'month',
    label: { th: 'เดือนนี้', en: 'This Month' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const startOfMonth = new Date(today);
      startOfMonth.setDate(1); // First day of current month
      return {
        startDate: getDateString(startOfMonth),
        endDate: getDateString(today)
      };
    }
  },
  {
    id: 'lastMonth',
    category: 'month',
    label: { th: 'เดือนที่แล้ว', en: 'Last Month' },
    icon: <Calendar size={14} />,
    getDateRange: () => {
      const today = new Date();
      const startOfLastMonth = new Date(today);
      startOfLastMonth.setMonth(today.getMonth() - 1, 1); // First day of last month
      const endOfLastMonth = new Date(today);
      endOfLastMonth.setDate(0); // Last day of previous month
      return {
        startDate: getDateString(startOfLastMonth),
        endDate: getDateString(endOfLastMonth)
      };
    }
  },
  {
    id: 'last90Days',
    category: 'range',
    label: { th: '90 วันที่ผ่านมา', en: 'Last 90 Days' },
    icon: <Clock size={14} />,
    getDateRange: () => {
      const today = new Date();
      const last90Days = new Date(today);
      last90Days.setDate(today.getDate() - 90);
      return {
        startDate: getDateString(last90Days),
        endDate: getDateString(today)
      };
    }
  }
];

// Helper function to get translated label
export const getPresetLabel = (preset: DatePreset, locale: 'th' | 'en' = 'th'): string => {
  return preset.label[locale];
};

// Helper function to find preset by ID
export const findPresetById = (id: DatePresetId): DatePreset | undefined => {
  return DATE_PRESETS.find(preset => preset.id === id);
};

// Helper function to group presets by category
export const getPresetsByCategory = (category?: DatePresetCategory): DatePreset[] => {
  if (!category) return DATE_PRESETS;
  return DATE_PRESETS.filter(preset => preset.category === category);
};
